import { createContext } from 'react';
import type { RefObject } from 'react';
import type { FloatingBoxLocation } from './FloatingBoxContextProvider';

export type BoxReferences = Record<string, HTMLDivElement | null>;

export type Box = {
  /**
   * The id of the box
   */
  id: string;
  /**
   * The element ref where the component will load
   */
  ref: RefObject<HTMLDivElement>;
  /**
   * The location of the box
   */
  location: FloatingBoxLocation;
};

type FloatingBoxContextInterface = {
  /**
   * Add a box to the floating box context.
   *
   * @param id The id of the box to add.
   * @param location The location of the box. Default is BOTTOM_LEFT.
   */
  addBox(id: string, location?: FloatingBoxLocation): void;
  /**
   * Remove a box from the floating box context.
   *
   * @param id The id of the box to remove.
   */
  removeBox(id: string): void;
  /**
   * Check if a box is in the floating box context.
   *
   * @param id The id of the box to check.
   */
  hasBox(id: string): boolean;
  /**
   * Clear all boxes from the floating box context.
   */
  clearBoxes: () => void;
  /**
   * Get a box reference by id.
   * @param id The id of the box to get.
   */
  getBoxReference(id: string): Box | undefined;
};

const FloatingBoxContext = createContext<FloatingBoxContextInterface>({
  addBox: () => null,
  removeBox: () => null,
  hasBox: () => false,
  clearBoxes: () => null,
  getBoxReference: () => null
});

export default FloatingBoxContext;
